import type { FunctionComponent } from 'react';
import React, { Suspense } from 'react';
import cx from 'classnames';
import { useLazyComponent } from '@trello/use-lazy-component';
import { SingleBoardViewFiltersProvider } from 'app/src/components/ViewFilters/SingleBoardViewFiltersProvider';
import { SingleBoardViewProvider } from 'app/src/components/BoardViewContext/SingleBoardViewProvider';
import { ErrorBoundary, ChunkLoadErrorBoundary } from '@trello/error-reporting';
import {
  useWorkspaceNavigation,
  useWorkspaceNavigationHidden,
} from 'app/src/components/WorkspaceNavigation';

import styles from './SingleBoardCalendarView.less';

interface SingleBoardCalendarViewProps {
  idBoard: string;
  navigateToCard: (id: string) => void;
}

export const SingleBoardCalendarView: FunctionComponent<SingleBoardCalendarViewProps> = ({
  idBoard,
  navigateToCard,
}: SingleBoardCalendarViewProps) => {
  const CalendarView = useLazyComponent(
    () => import(/* webpackChunkName: "calendar-view" */ './CalendarView'),
    { namedImport: 'CalendarView' },
  );

  const [{ expanded: workspaceNavigationExpanded }] = useWorkspaceNavigation();
  const [
    { hidden: workspaceNavigationHidden },
  ] = useWorkspaceNavigationHidden();

  return (
    <div
      className={cx(styles.container, {
        [styles.collapsedWorkspaceNavigation]:
          !workspaceNavigationHidden && !workspaceNavigationExpanded,
      })}
    >
      <ErrorBoundary
        tags={{
          ownershipArea: 'trello-panorama',
          feature: 'Calendar View',
        }}
      >
        <Suspense fallback={null}>
          <ChunkLoadErrorBoundary fallback={null}>
            <SingleBoardViewFiltersProvider idBoard={idBoard}>
              <SingleBoardViewProvider
                idBoard={idBoard}
                isDateBasedView={true}
                navigateToCard={navigateToCard}
                includeChecklistItems={true}
              >
                <CalendarView />
              </SingleBoardViewProvider>
            </SingleBoardViewFiltersProvider>
          </ChunkLoadErrorBoundary>
        </Suspense>
      </ErrorBoundary>
    </div>
  );
};
